import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/common/DataTable';
import { useHeader } from '@/components/context/HeaderContext';
import {
  fetchUserPlaybackReport,
  IUserActivityReport,
  fetchPostHogUserEvents,
  IUserEventReport,
  fetchPostHogUserGiftClicks,
  IGiftOneReport,
  fetchPostHogUserGetClicks,
  IGetOneReport,
} from '@/store/api/posthogAnalytics';
import { ArrowLeft, Music, Clock, Calendar, Award, Download, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function UserActivityLogsPage() {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { setHeader } = useHeader();

  const stateData = location.state as { email?: string; fullName?: string } | null;
  const userEmail = stateData?.email || '';
  const userFullName = stateData?.fullName || 'User';

  const [activeTab, setActiveTab] = useState<'playback' | 'events' | 'gift' | 'get'>('playback');

  // Playback logs states
  const [userReport, setUserReport] = useState<IUserActivityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playbackPage, setPlaybackPage] = useState(1);
  const [playbackTake, setPlaybackTake] = useState(10);
  const [playbackSearch, setPlaybackSearch] = useState('');

  // User events states
  const [userEvents, setUserEvents] = useState<IUserEventReport[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventPage, setEventPage] = useState(1);
  const [eventTake, setEventTake] = useState(10);
  const [eventSearch, setEventSearch] = useState('');

  // Gift One Clicks states
  const [giftClicks, setGiftClicks] = useState<IGiftOneReport[]>([]);
  const [giftLoading, setGiftLoading] = useState(true);
  const [giftPage, setGiftPage] = useState(1);
  const [giftTake, setGiftTake] = useState(10);
  const [giftSearch, setGiftSearch] = useState('');

  // Get One Clicks states
  const [getClicks, setGetClicks] = useState<IGetOneReport[]>([]);
  const [getLoading, setGetLoading] = useState(true);
  const [getPage, setGetPage] = useState(1);
  const [getTake, setGetTake] = useState(10);
  const [getSearch, setGetSearch] = useState('');

  useEffect(() => {
    setHeader({
      type: 'user',
      props: {
        title: 'Consumption Logs',
        lastUpdated: new Date().toLocaleString(),
        showSort: false,
        showFilter: false,
        showMore: false,
      },
    });
    return () => setHeader(null);
  }, [setHeader]);

  // Fetch playback logs
  useEffect(() => {
    if (!userEmail) {
      setError('User metadata not provided. Please navigate from the User Management page.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    fetchUserPlaybackReport(userEmail, userFullName)
      .then((report) => {
        setUserReport(report);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Failed to fetch detailed consumption logs from telemetry services.');
        setLoading(false);
      });
  }, [userEmail, userFullName]);

  // Fetch custom events
  useEffect(() => {
    if (!userEmail) return;

    setEventsLoading(true);
    fetchPostHogUserEvents(userEmail)
      .then((events) => {
        setUserEvents(events);
        setEventsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch user events:', err);
        setEventsLoading(false);
      });
  }, [userEmail]);

  // Fetch Gift One clicks
  useEffect(() => {
    if (!userEmail) return;

    setGiftLoading(true);
    fetchPostHogUserGiftClicks(userEmail)
      .then((data) => {
        setGiftClicks(data);
        setGiftLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch gift clicks:', err);
        setGiftLoading(false);
      });
  }, [userEmail]);

  // Fetch Get One clicks
  useEffect(() => {
    if (!userEmail) return;

    setGetLoading(true);
    fetchPostHogUserGetClicks(userEmail)
      .then((data) => {
        setGetClicks(data);
        setGetLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch get clicks:', err);
        setGetLoading(false);
      });
  }, [userEmail]);

  const formatDuration = (seconds: number) => {
    if (seconds === 0) return '0s';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.round(seconds % 60);

    const parts = [];
    if (hrs > 0) parts.push(`${hrs}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

    return parts.join(' ');
  };

  const exportUserReport = () => {
    if (!userReport) return;

    const headers = [
      'Song/Mantra Title',
      'Total Plays',
      'Listening Duration (Seconds)',
      'Last Listened At',
    ];
    const rows = userReport.songs.map((song) => [
      `"${song.title.replace(/"/g, '""')}"`,
      song.plays,
      song.durationSeconds.toFixed(1),
      `"${new Date(song.lastListened).toLocaleString()}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [
        ['User Name', userReport.name],
        ['User Email', userReport.email],
        ['User Tier', userReport.userTier.toUpperCase()],
        [],
        headers,
        ...rows,
      ]
        .map((e) => e.join(','))
        .join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${userReport.name.replace(/\s+/g, '_')}_playback_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportUserEventsReport = () => {
    if (userEvents.length === 0) return;

    const headers = ['S.No', 'Event Name', 'URL/Path', 'IP Address', 'Timestamp'];
    const rows = userEvents.map((ev, idx) => [
      idx + 1,
      `"${ev.event.replace(/"/g, '""')}"`,
      `"${ev.path.replace(/"/g, '""')}"`,
      ev.ip,
      `"${new Date(ev.timestamp).toLocaleString()}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [['User Name', userFullName], ['User Email', userEmail], [], headers, ...rows]
        .map((e) => e.join(','))
        .join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${userFullName.replace(/\s+/g, '_')}_user_events_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportGiftReport = () => {
    if (giftClicks.length === 0) return;

    const headers = ['S.No', 'User Name', 'Email', 'Mantra Name', 'Button Link', 'Clicked At'];
    const rows = giftClicks.map((click, idx) => [
      idx + 1,
      `"${click.name.replace(/"/g, '""')}"`,
      click.email,
      `"${click.mantraName.replace(/"/g, '""')}"`,
      `"${click.link.replace(/"/g, '""')}"`,
      `"${new Date(click.timestamp).toLocaleString()}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' + [headers, ...rows].map((e) => e.join(',')).join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${userFullName.replace(/\s+/g, '_')}_gift_one_clicks.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportGetReport = () => {
    if (getClicks.length === 0) return;

    const headers = ['S.No', 'User Name', 'Email', 'Mantra Name', 'Button Link', 'Clicked At'];
    const rows = getClicks.map((click, idx) => [
      idx + 1,
      `"${click.name.replace(/"/g, '""')}"`,
      click.email,
      `"${click.mantraName.replace(/"/g, '""')}"`,
      `"${click.link.replace(/"/g, '""')}"`,
      `"${new Date(click.timestamp).toLocaleString()}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' + [headers, ...rows].map((e) => e.join(',')).join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${userFullName.replace(/\s+/g, '_')}_get_one_clicks.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const logColumns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'sNo',
        header: 'S.No',
        cell: ({ row }) => <div className="text-[#1E0036] font-medium">{row.getValue('sNo')}</div>,
      },
      {
        accessorKey: 'title',
        header: 'Song/Mantra Title',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Music className="w-4 h-4 text-[#8B5CF6] shrink-0" />
            <span className="font-semibold text-[#1E0036] min-w-[150px] text-xs sm:text-[16px]">
              {row.getValue('title')}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'plays',
        header: 'Total Plays',
        cell: ({ row }) => {
          const plays = row.getValue('plays') as number;
          return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 whitespace-nowrap">
              {plays} plays
            </span>
          );
        },
      },
      {
        accessorKey: 'durationSeconds',
        header: 'Listening Duration',
        cell: ({ row }) => {
          const seconds = row.getValue('durationSeconds') as number;
          return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#8B5CF6]/10 text-violet-700 border border-[#8B5CF6]/10">
              <Clock className="w-3.5 h-3.5 mr-1" />
              {formatDuration(seconds)}
            </span>
          );
        },
      },
      {
        accessorKey: 'lastListened',
        header: 'Last Listened At',
        cell: ({ row }) => {
          const dateStr = row.getValue('lastListened') as string;
          return (
            <div className="flex items-center gap-1.5 text-sm text-[#62556B] font-mono">
              <Calendar className="w-4 h-4 text-violet-400 shrink-0" />
              <span>{new Date(dateStr).toLocaleString()}</span>
            </div>
          );
        },
      },
    ],
    []
  );

  const eventColumns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'sNo',
        header: 'S.No',
        cell: ({ row }) => <div className="text-[#1E0036] font-medium">{row.getValue('sNo')}</div>,
      },
      {
        accessorKey: 'event',
        header: 'Event Name',
        cell: ({ row }) => (
          <span className="font-semibold text-[#1E0036] min-w-[150px] text-xs sm:text-[16px] whitespace-nowrap">
            {row.getValue('event')}
          </span>
        ),
      },
      {
        accessorKey: 'path',
        header: 'URL/Path',
        cell: ({ row }) => (
          <span className="text-[#62556B] text-xs font-mono">{row.getValue('path')}</span>
        ),
      },
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        cell: ({ row }) => {
          const dateStr = row.getValue('timestamp') as string;
          return (
            <div className="flex items-center gap-1.5 text-sm text-[#62556B] font-mono">
              <Calendar className="w-4 h-4 text-violet-400 shrink-0" />
              <span>{new Date(dateStr).toLocaleString()}</span>
            </div>
          );
        },
      },
    ],
    []
  );

  const giftColumns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'sNo',
        header: 'S.No',
        cell: ({ row }) => <div className="text-[#1E0036] font-medium">{row.getValue('sNo')}</div>,
      },
      {
        accessorKey: 'name',
        header: 'User Name',
        cell: ({ row }) => (
          <span className="font-semibold text-[#1E0036]">{row.getValue('name')}</span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => <span className="text-[#62556B]">{row.getValue('email')}</span>,
      },
      {
        accessorKey: 'mantraName',
        header: 'Mantra Name',
        cell: ({ row }) => (
          <span className="font-semibold text-[#1E0036]">{row.getValue('mantraName')}</span>
        ),
      },
      {
        accessorKey: 'link',
        header: 'Button Link',
        cell: ({ row }) => {
          const link = row.getValue('link') as string;
          return (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline text-xs font-mono break-all max-w-[200px] inline-block"
            >
              {link}
            </a>
          );
        },
      },
      {
        accessorKey: 'timestamp',
        header: 'Clicked At',
        cell: ({ row }) => {
          const dateStr = row.getValue('timestamp') as string;
          return (
            <div className="flex items-center gap-1.5 text-sm text-[#62556B] font-mono">
              <Calendar className="w-4 h-4 text-violet-400 shrink-0" />
              <span>{new Date(dateStr).toLocaleString()}</span>
            </div>
          );
        },
      },
    ],
    []
  );

  const getColumns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'sNo',
        header: 'S.No',
        cell: ({ row }) => <div className="text-[#1E0036] font-medium">{row.getValue('sNo')}</div>,
      },
      {
        accessorKey: 'name',
        header: 'User Name',
        cell: ({ row }) => (
          <span className="font-semibold text-[#1E0036]">{row.getValue('name')}</span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => <span className="text-[#62556B]">{row.getValue('email')}</span>,
      },
      {
        accessorKey: 'mantraName',
        header: 'Mantra Name',
        cell: ({ row }) => (
          <span className="font-semibold text-[#1E0036]">{row.getValue('mantraName')}</span>
        ),
      },
      {
        accessorKey: 'link',
        header: 'Button Link',
        cell: ({ row }) => {
          const link = row.getValue('link') as string;
          return (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline text-xs font-mono break-all max-w-[200px] inline-block"
            >
              {link}
            </a>
          );
        },
      },
      {
        accessorKey: 'timestamp',
        header: 'Clicked At',
        cell: ({ row }) => {
          const dateStr = row.getValue('timestamp') as string;
          return (
            <div className="flex items-center gap-1.5 text-sm text-[#62556B] font-mono">
              <Calendar className="w-4 h-4 text-violet-400 shrink-0" />
              <span>{new Date(dateStr).toLocaleString()}</span>
            </div>
          );
        },
      },
    ],
    []
  );

  // Filtering playback logs
  const filteredSongs =
    userReport?.songs.filter((song) =>
      song.title.toLowerCase().includes(playbackSearch.toLowerCase())
    ) || [];

  const playbackWithSNo = React.useMemo(() => {
    return filteredSongs.map((song, index) => ({
      ...song,
      sNo: index + 1,
    }));
  }, [filteredSongs]);

  // Filtering user events
  const filteredEvents = userEvents.filter(
    (ev) =>
      ev.event.toLowerCase().includes(eventSearch.toLowerCase()) ||
      ev.path.toLowerCase().includes(eventSearch.toLowerCase()) ||
      ev.ip.toLowerCase().includes(eventSearch.toLowerCase())
  );

  const eventsWithSNo = React.useMemo(() => {
    return filteredEvents.map((ev, index) => ({
      ...ev,
      sNo: index + 1,
    }));
  }, [filteredEvents]);

  // Filtering gift clicks
  const filteredGifts = giftClicks.filter(
    (click) =>
      click.name.toLowerCase().includes(giftSearch.toLowerCase()) ||
      click.email.toLowerCase().includes(giftSearch.toLowerCase()) ||
      click.mantraName.toLowerCase().includes(giftSearch.toLowerCase()) ||
      click.link.toLowerCase().includes(giftSearch.toLowerCase())
  );

  const giftsWithSNo = React.useMemo(() => {
    return filteredGifts.map((click, index) => ({
      ...click,
      sNo: index + 1,
    }));
  }, [filteredGifts]);

  // Filtering get clicks
  const filteredGets = getClicks.filter(
    (click) =>
      click.name.toLowerCase().includes(getSearch.toLowerCase()) ||
      click.email.toLowerCase().includes(getSearch.toLowerCase()) ||
      click.mantraName.toLowerCase().includes(getSearch.toLowerCase()) ||
      click.link.toLowerCase().includes(getSearch.toLowerCase())
  );

  const getsWithSNo = React.useMemo(() => {
    return filteredGets.map((click, index) => ({
      ...click,
      sNo: index + 1,
    }));
  }, [filteredGets]);

  return (
    <div className="p-4 md:p-8 min-h-screen space-y-6 font-sans">
      {/* Navigation & Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <button
          onClick={() => navigate('/user-management')}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#62556B] hover:text-[#1E0036] transition-colors cursor-pointer group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to User Management
        </button>

        {activeTab === 'playback'
          ? userReport &&
            userReport.songs.length > 0 && (
              <Button
                onClick={exportUserReport}
                className="max-w-[200px] bg-[#1E0036] hover:bg-lightPurple text-white px-5 h-10 rounded-lg transition-all flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export Playback CSV
              </Button>
            )
          : activeTab === 'events'
            ? userEvents.length > 0 && (
                <Button
                  onClick={exportUserEventsReport}
                  className="max-w-[200px] bg-[#1E0036] hover:bg-lightPurple text-white px-5 h-10 rounded-lg transition-all flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export Events CSV
                </Button>
              )
            : activeTab === 'gift'
              ? giftClicks.length > 0 && (
                  <Button
                    onClick={exportGiftReport}
                    className="max-w-[200px] bg-[#1E0036] hover:bg-lightPurple text-white px-5 h-10 rounded-lg transition-all flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export Gift Clicks CSV
                  </Button>
                )
              : getClicks.length > 0 && (
                  <Button
                    onClick={exportGetReport}
                    className="max-w-[200px] bg-[#1E0036] hover:bg-lightPurple text-white px-5 h-10 rounded-lg transition-all flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export Get Clicks CSV
                  </Button>
                )}
      </div>

      {/* User Information Panel */}
      <div className="bg-white rounded-[20px] shadow-sm border border-[#E9E9E9] p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-violet-600 to-indigo-600 flex items-center justify-center text-white font-bold text-xl shadow-md uppercase">
            {userFullName.charAt(0)}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#1E0036]">{userFullName}</h2>
            <p className="text-sm text-[#8E87A4] font-noto mt-0.5 max-w-[150px] truncate sm:max-w-full ">
              {userEmail || 'No Email Available'}
            </p>
          </div>
        </div>

        {/* User Tier Status */}
        {!loading && userReport && (
          <div className="flex items-center gap-2 bg-[#FAF4FF] border border-[#F0EEF9] px-4 py-3 rounded-xl">
            <span className="text-xs font-semibold text-[#62556B] uppercase tracking-wider">
              User Status:
            </span>
            {userReport.userTier === 'premium' ? (
              <span className="inline-flex items-center px-3.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-600 border border-amber-500/20">
                <Award className="w-3.5 h-3.5 mr-1" />
                Premium User
              </span>
            ) : (
              <span className="inline-flex items-center px-3.5 py-1 rounded-full text-xs font-bold bg-[#8B5CF6]/10 text-violet-700 border border-[#8B5CF6]/20">
                Free User
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#F0EEF9] pb-px">
        <button
          onClick={() => setActiveTab('playback')}
          className={`pb-4 px-3 sm:px-6 font-semibold text-sm transition-all border-b-2 cursor-pointer ${
            activeTab === 'playback'
              ? 'border-[#8B5CF6] text-[#8B5CF6]'
              : 'border-transparent text-[#62556B] hover:text-[#1E0036]'
          }`}
        >
          Playback logs list
        </button>
        <button
          onClick={() => setActiveTab('events')}
          className={`pb-4 px-3 sm:px-6 font-semibold text-sm transition-all border-b-2 cursor-pointer ${
            activeTab === 'events'
              ? 'border-[#8B5CF6] text-[#8B5CF6]'
              : 'border-transparent text-[#62556B] hover:text-[#1E0036]'
          }`}
        >
          User Events
        </button>
        <button
          onClick={() => setActiveTab('gift')}
          className={`pb-4 px-3 sm:px-6 font-semibold text-sm transition-all border-b-2 cursor-pointer ${
            activeTab === 'gift'
              ? 'border-[#8B5CF6] text-[#8B5CF6]'
              : 'border-transparent text-[#62556B] hover:text-[#1E0036]'
          }`}
        >
          Gift One Clicks
        </button>
        <button
          onClick={() => setActiveTab('get')}
          className={`pb-4 px-3 sm:px-6 font-semibold text-sm transition-all border-b-2 cursor-pointer ${
            activeTab === 'get'
              ? 'border-[#8B5CF6] text-[#8B5CF6]'
              : 'border-transparent text-[#62556B] hover:text-[#1E0036]'
          }`}
        >
          Get One Clicks
        </button>
      </div>

      {/* Tab Contents */}
      {activeTab === 'playback' ? (
        error ? (
          <div className="bg-red-50 border border-red-200/50 p-6 rounded-[20px] flex items-center gap-3 text-red-700 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : (
          <DataTable
            columns={logColumns}
            data={playbackWithSNo}
            title="Playback logs list"
            totalCount={playbackWithSNo.length}
            page={playbackPage}
            take={playbackTake}
            loading={loading}
            search={playbackSearch}
            onPageChange={setPlaybackPage}
            onTakeChange={setPlaybackTake}
            enableSearch
            searchPlaceholder="Search by song/mantra title..."
            onSearchChange={setPlaybackSearch}
          />
        )
      ) : activeTab === 'events' ? (
        <DataTable
          columns={eventColumns}
          data={eventsWithSNo}
          title="User Events"
          totalCount={eventsWithSNo.length}
          page={eventPage}
          take={eventTake}
          loading={eventsLoading}
          search={eventSearch}
          onPageChange={setEventPage}
          onTakeChange={setEventTake}
          enableSearch
          searchPlaceholder="Search by event name, path, or IP..."
          onSearchChange={setEventSearch}
        />
      ) : activeTab === 'gift' ? (
        <DataTable
          columns={giftColumns}
          data={giftsWithSNo}
          title="Gift One Button Clicks"
          totalCount={giftsWithSNo.length}
          page={giftPage}
          take={giftTake}
          loading={giftLoading}
          search={giftSearch}
          onPageChange={setGiftPage}
          onTakeChange={setGiftTake}
          enableSearch
          searchPlaceholder="Search by mantra or link..."
          onSearchChange={setGiftSearch}
        />
      ) : (
        <DataTable
          columns={getColumns}
          data={getsWithSNo}
          title="Get One Button Clicks"
          totalCount={getsWithSNo.length}
          page={getPage}
          take={getTake}
          loading={getLoading}
          search={getSearch}
          onPageChange={setGetPage}
          onTakeChange={setGetTake}
          enableSearch
          searchPlaceholder="Search by mantra or link..."
          onSearchChange={setGetSearch}
        />
      )}
    </div>
  );
}
